import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Autocomplete,
  TextField,
  Popover,
  Checkbox,
  Chip
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import axios from 'axios';
import apiService from '../../services/actions';
import PropTypes from 'prop-types';

const ORDER_TYPE_OPTIONS = [
  { value: "All", label: "All" },
  // { value: "Collection Center", label: "B2B (Collection Centers)" },
  // { value: "b2b2C", label: "B2B2C" },
  { value: "Lab", label: "B2C (eClinics)" },
  { value: "d2c", label: "D2C" },
];

const DATE_RANGE_OPTIONS = [
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "custom", label: "Custom Range" },
];

const TEST_TYPE_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Pathology", label: "Pathology" },
  { value: "Radiology", label: "Radiology" },
];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SelectAllCheckbox = ({ isAllSelected, isIndeterminate, onSelectAll, color = "primary" }) => {
  return (
    <Checkbox
      icon={icon}
      checkedIcon={checkedIcon}
      indeterminateIcon={<IndeterminateCheckBoxIcon />}
      checked={isAllSelected}
      indeterminate={isIndeterminate}
      onChange={onSelectAll}
      color={color}
    />
  );
};

SelectAllCheckbox.propTypes = {
  isAllSelected: PropTypes.bool.isRequired,
  isIndeterminate: PropTypes.bool.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  color: PropTypes.string,
};
const getIsSelected = (option, value, hasAllSelected, getOptionValue) => {
  const val = getOptionValue(option);

  if (val === "All") return hasAllSelected;
  if (hasAllSelected) return true;

  return value.some(v => getOptionValue(v) === val);
};
const toggleOption = ({
  option,
  value,
  hasAllSelected,
  allOption,
  otherOptions,
  getOptionValue,
  onChange
}) => {
  const val = getOptionValue(option);

  if (val === "All") {
    if (hasAllSelected) onChange([]);
    else onChange([allOption]);
    return;
  }

  if (hasAllSelected) {
    const updated = otherOptions.filter(
      (opt) => getOptionValue(opt) !== val
    );
    onChange(updated);
    return;
  }

  const exists = value.some(v => getOptionValue(v) === val);

  let updated;
  if (exists) {
    updated = value.filter(v => getOptionValue(v) !== val);
  } else {
    updated = [...value, option];
  }

  if (updated.length === otherOptions.length) {
    onChange([allOption]);
  } else {
    onChange(updated);
  }
};
const handleOptionToggle = (option, config) => {
  toggleOption({
    option,
    ...config
  });
};
const MultiSelectDropdown = ({
  options,
  value,
  onChange,
  label,
  getOptionLabel,
  getOptionValue,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const allOption = options.find(opt => getOptionValue(opt) === "All");
  const otherOptions = options.filter(opt => getOptionValue(opt) !== "All");

  const hasAllSelected = value.some(v => getOptionValue(v) === "All");

const isSelected = (option) => {
  return getIsSelected(option, value, hasAllSelected, getOptionValue);
};

const handleToggle = (option) => {
  handleOptionToggle(option, {
    value,
    hasAllSelected,
    allOption,
    otherOptions,
    getOptionValue,
    onChange
  });
};

  const handleDelete = (item, e) => {
    if (e) e.stopPropagation();
    const val = getOptionValue(item);
    if (val === "All") {
      onChange([]);
      return;
    }
    if (hasAllSelected) {
      onChange(otherOptions.filter((opt) => getOptionValue(opt) !== val));
      return;
    }
    const updated = value.filter((v) => getOptionValue(v) !== val);
    onChange(updated);
  };

  return (
    <>
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel sx={{ fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 }, "&.Mui-focused": { color: "#004c97" } }}>{label}</InputLabel>
        <Select
          open={false}
          value={value}
          displayEmpty
          label={label}
          onClick={handleClick}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            height: 34,
            borderRadius: "7px",
            bgcolor: "#ffffff",
            fontSize: "0.8125rem",
            "& .MuiSelect-select": {
              py: "4px !important",
              display: "flex",
              alignItems: "center",
            },
            "& fieldset": { borderColor: "#CBD5E1" },
            "&:hover fieldset": { borderColor: "#94A3B8" },
            "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
          }}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) return "";

            const hasAll = selected.some(v => getOptionValue(v) === "All");

            if (hasAll) {
              return (
                <Chip
                  label="All"
                  size="small"
                  sx={{ height: 20, fontSize: "0.72rem", bgcolor: "#EFF6FF", color: "#004c97", fontWeight: 600 }}
                  onDelete={(e) => handleDelete(allOption || { value: "All", label: "All" }, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              );
            }

            const chips = selected.slice(0, 2);

            return (
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                {chips.map((item) => (
                  <Chip
                    key={getOptionValue(item)}
                    label={getOptionLabel(item)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.72rem",
                      maxWidth: selected.length > 1 ? 80 : 120,
                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 0.6 },
                    }}
                    onDelete={(e) => handleDelete(item, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ))}
                {selected.length > 2 && (
                  <Chip
                    label={`+${selected.length - 2}`}
                    size="small"
                    sx={{ height: 20, fontSize: "0.72rem", bgcolor: "#F1F5F9", color: "#475569", fontWeight: 600 }}
                  />
                )}
              </Box>
            );
          }}
        />
      </FormControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1, width: anchorEl?.offsetWidth || 300 }}>
          {options.map((option) => (
            <MenuItem
              key={getOptionValue(option)}
              onClick={() => handleToggle(option)}
            >
              <Checkbox
                checked={isSelected(option)}
                size="small"
                sx={{ mr: 1 }}
              />
              {getOptionLabel(option)}
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const SearchableMultiSelectDropdown = ({
  options,
  value,
  onChange,
  label,
  getOptionLabel,
  getOptionValue,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const allOption = options.find(opt => getOptionValue(opt) === "All");
  const otherOptions = options.filter(opt => getOptionValue(opt) !== "All");

  const hasAllSelected = value.some(v => getOptionValue(v) === "All");

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(opt =>
      getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, getOptionLabel]);

const checkIsSelected = (option) => {
  return getIsSelected(option, value, hasAllSelected, getOptionValue);
};

const performToggle = (option) => {
  toggleOption({
    option,
    value,
    hasAllSelected,
    allOption,
    otherOptions,
    getOptionValue,
    onChange
  });
};

  const handleDelete = (item, e) => {
    if (e) e.stopPropagation();
    const val = getOptionValue(item);
    if (val === "All") {
      onChange([]);
      return;
    }
    if (hasAllSelected) {
      onChange(otherOptions.filter((opt) => getOptionValue(opt) !== val));
      return;
    }
    const updated = value.filter((v) => getOptionValue(v) !== val);
    onChange(updated);
  };

  return (
    <>
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel sx={{ fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 }, "&.Mui-focused": { color: "#004c97" } }}>{label}</InputLabel>
        <Select
          open={false}
          value={value}
          displayEmpty
          label={label}
          onClick={handleClick}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            height: 34,
            borderRadius: "7px",
            bgcolor: "#ffffff",
            fontSize: "0.8125rem",
            "& .MuiSelect-select": {
              py: "4px !important",
              display: "flex",
              alignItems: "center",
            },
            "& fieldset": { borderColor: "#CBD5E1" },
            "&:hover fieldset": { borderColor: "#94A3B8" },
            "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
          }}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) return "";

            const hasAll = selected.some(v => getOptionValue(v) === "All");

            if (hasAll) {
              return (
                <Chip
                  label="All"
                  size="small"
                  sx={{ height: 20, fontSize: "0.72rem", bgcolor: "#EFF6FF", color: "#004c97", fontWeight: 600 }}
                  onDelete={(e) => handleDelete(allOption || { labCode: "All", labName: "All" }, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              );
            }

            const chips = selected.slice(0, 2);

            return (
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                {chips.map((item) => (
                  <Chip
                    key={getOptionValue(item)}
                    label={getOptionLabel(item)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.72rem",
                      maxWidth: selected.length > 1 ? 80 : 120,
                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 0.6 },
                    }}
                    onDelete={(e) => handleDelete(item, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ))}
                {selected.length > 2 && (
                  <Chip
                    label={`+${selected.length - 2}`}
                    size="small"
                    sx={{ height: 20, fontSize: "0.72rem", bgcolor: "#F1F5F9", color: "#475569", fontWeight: 600 }}
                  />
                )}
              </Box>
            );
          }}
        />
      </FormControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1, width: anchorEl?.offsetWidth || 300 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1 }}
          />

          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {filteredOptions.map((option) => (
              <MenuItem
                key={getOptionValue(option)}
                onClick={() => performToggle(option)}
              >
                <Checkbox
                  checked={checkIsSelected(option)}
                  size="small"
                  sx={{ mr: 1 }}
                />
                {getOptionLabel(option)}
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

SearchableMultiSelectDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default function Dashboard() {
  const [orderType, setOrderType] = useState([ORDER_TYPE_OPTIONS[0]]);
  const [dateRange, setDateRange] = useState("last7");
  const [searchFilters, setSearchFilters] = useState({
    fromDate: dayjs().subtract(7, 'day'),
    toDate: dayjs(),
    testType: 'All',
    selectedEclinics: [],
    selectedLabs: [],
    selectedCcs: []
  });

  const [labList, setLabList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [eclinicData, setEclinicData] = useState([]);
  const [labSearchLoading, setLabSearchLoading] = useState(false);
  const [ccSearchLoading, setCcSearchLoading] = useState(false);
  
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [orderOverviewData, setOrderOverviewData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [trendStats, setTrendStats] = useState({
    ordersReceived: 0,
    reportsUploaded: 0,
    completionRate: 0
  });
  const [overviewData, setOverviewData] = useState({
    completed: 0,
    partial: 0,
    pending: 0,
    cancelled: 0,
    total: 0
  });

  const [trendView, setTrendView] = useState('Week');

  const s_token = localStorage.getItem('access_token');

  const getDateRange = (rangeType) => {
    const today = dayjs();
    if (rangeType === "last7") {
      return {
        fromDate: today.subtract(7, 'day'),
        toDate: today
      };
    }
    if (rangeType === "last30") {
      return {
        fromDate: today.subtract(30, 'day'),
        toDate: today
      };
    }
    return {
      fromDate: searchFilters.fromDate,
      toDate: searchFilters.toDate
    };
  };

  const formatDateToMMDDYYYY = (date) => {
    return dayjs(date).format('MM/DD/YYYY');
  };

  const buildCommonPayload = () => {
    const dateRangeValues = getDateRange(dateRange);
    
    const payload = {
      fromDate: dateRangeValues.fromDate ? formatDateToMMDDYYYY(dateRangeValues.fromDate) : undefined,
      toDate: dateRangeValues.toDate ? formatDateToMMDDYYYY(dateRangeValues.toDate) : undefined,
      testType: searchFilters.testType === 'All' ? undefined : searchFilters.testType
    };

       const orderTypeValues = new Set(orderType.map(opt => opt.value));
    const diagnosticType = orderTypeValues.has("All") ? undefined : Array.from(orderTypeValues).join(",");
    payload.orderType = diagnosticType;

    if (searchFilters.selectedEclinics?.length > 0 && !searchFilters.selectedEclinics.some(e => e?.eclinicCode === "All")) {
      payload.eclinicId = searchFilters.selectedEclinics.map((e) => e.eclinicCode).join(",");
    }

    if (searchFilters.selectedLabs?.length > 0 && !searchFilters.selectedLabs.some(l => l?.labCode === "All")) {
      payload.hospitalId = searchFilters.selectedLabs.map((l) => l.labCode).join(",");
    }

    if (searchFilters.selectedCcs?.length > 0 && !searchFilters.selectedCcs.some(cc => cc?.labCode === "All")) {
      payload.ccHospitalId = searchFilters.selectedCcs.map((cc) => cc.labCode).join(",");
    }

    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });
    
    return payload;
  };

  const navigateToDetailedOrders = (filters = {}) => {
    const dateRangeValues = getDateRange(dateRange);
    
    const baseFilters = {
      fromDate: dateRangeValues.fromDate ? formatDateToMMDDYYYY(dateRangeValues.fromDate) : '',
      toDate: dateRangeValues.toDate ? formatDateToMMDDYYYY(dateRangeValues.toDate) : '',
      orderType: orderType.map(opt => opt.value).join(','),
      testType: searchFilters.testType || '',
      ...filters
    };

    if (searchFilters.selectedEclinics?.length > 0 && !searchFilters.selectedEclinics.some(e => e?.eclinicCode === "All")) {
      baseFilters.CHEId = searchFilters.selectedEclinics.map((e) => e.eclinicCode).join(",");
    }

    if (searchFilters.selectedLabs?.length > 0 && !searchFilters.selectedLabs.some(l => l?.labCode === "All")) {
      baseFilters.hospitalId = searchFilters.selectedLabs.map((l) => l.labCode).join(",");
    }

    if (searchFilters.selectedCcs?.length > 0 && !searchFilters.selectedCcs.some(cc => cc?.labCode === "All")) {
      baseFilters.ccHospitalId = searchFilters.selectedCcs.map((cc) => cc.labCode).join(",");
    }

    Object.keys(baseFilters).forEach(key => {
      if (baseFilters[key] === '' || baseFilters[key] === undefined) {
        delete baseFilters[key];
      }
    });

    globalThis.location.href = `/completedOrders?${new URLSearchParams(baseFilters).toString()}`;
  };

  const handleTrendBarClickWeek = (dayName, barType) => {
    const dateRangeValues = getDateRange(dateRange);
    const fromDate = dateRangeValues.fromDate;
    const toDate = dateRangeValues.toDate;
    
    const daysInRange = [];
    let currentDate = fromDate;
    
    while (currentDate <= toDate) {
      daysInRange.push({
        date: currentDate,
        dayName: currentDate.format('dddd')
      });
      currentDate = currentDate.add(1, 'day');
    }
    
    const matchingDay = daysInRange.find(day => day.dayName === dayName);
    
    if (matchingDay) {
      const filters = {
        fromDate: formatDateToMMDDYYYY(matchingDay.date),
        toDate: formatDateToMMDDYYYY(matchingDay.date)
      };

      if (barType === 'reportsUploaded') {
        filters.orderStatus = 'Completed';
      }

      navigateToDetailedOrders(filters);
      return;
    }
    
    const today = dayjs();
    const dayIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(dayName);
    if (dayIndex !== -1) {
      const currentDayIndex = today.day();
      const diff = dayIndex - currentDayIndex;
      const targetDate = today.add(diff, 'day');
      
      const filters = {
        fromDate: formatDateToMMDDYYYY(targetDate),
        toDate: formatDateToMMDDYYYY(targetDate)
      };

      if (barType === 'reportsUploaded') {
        filters.orderStatus = 'Completed';
      }

      navigateToDetailedOrders(filters);
    }
  };

  const parseDateRangeFromLabel = (weekLabel) => {
    const patterns = [
      { regex: /(\w+ \d+)\s*-\s*(\w+ \d+)/, handler: (match) => {
        const currentYear = dayjs().year();
        let fromDateStr = match[1].trim();
        let toDateStr = match[2].trim();
        
        if (!fromDateStr.match(/\d{4}/)) fromDateStr += `, ${currentYear}`;
        if (!toDateStr.match(/\d{4}/)) toDateStr += `, ${currentYear}`;
        
        return { fromDate: dayjs(fromDateStr), toDate: dayjs(toDateStr) };
      }},
      { regex: /^(\w+ \d+)\s*-\s*(\w+ \d+)$/, handler: (match) => {
        const currentYear = dayjs().year();
        let fromDateStr = match[1].trim();
        let toDateStr = match[2].trim();
        
        if (!fromDateStr.match(/\d{4}/)) fromDateStr += `, ${currentYear}`;
        if (!toDateStr.match(/\d{4}/)) toDateStr += `, ${currentYear}`;
        
        return { fromDate: dayjs(fromDateStr), toDate: dayjs(toDateStr) };
      }},
      { regex: /(\d+)\s*-\s*(\d+)\s*(\w+)/, handler: (match) => {
        const currentYear = dayjs().year();
        const startDay = match[1];
        const endDay = match[2];
        const monthName = match[3];
        
        const fromDate = dayjs(`${monthName} ${startDay}, ${currentYear}`);
        const toDate = dayjs(`${monthName} ${endDay}, ${currentYear}`);
        
        return { fromDate, toDate };
      }},
      { regex: /(\w+ \d+, \d+)\s*-\s*(\w+ \d+, \d+)/, handler: (match) => {
        const fromDate = dayjs(match[1].trim());
        const toDate = dayjs(match[2].trim());
        
        return { fromDate, toDate };
      }}
    ];

    for (const pattern of patterns) {
      const weekMatch = weekLabel.match(pattern.regex);
      if (!weekMatch) continue;

      const { fromDate, toDate } = pattern.handler(weekMatch);
      if (fromDate.isValid() && toDate.isValid()) {
        return { fromDate, toDate };
      }
    }

    return null;
  };

  const createFilters = (fromDate, toDate, barType) => {
    const filters = {
      fromDate: formatDateToMMDDYYYY(fromDate),
      toDate: formatDateToMMDDYYYY(toDate)
    };

    if (barType === 'reportsUploaded') {
      filters.orderStatus = 'Completed';
    }

    return filters;
  };

  const getFallbackDateRange = () => {
    return {
      fromDate: dayjs().startOf('week'),
      toDate: dayjs().endOf('week')
    };
  };

  const handleTrendBarClickMonth = (weekLabel, barType) => {
    const parsedDates = parseDateRangeFromLabel(weekLabel);
    
    if (parsedDates) {
      const filters = createFilters(parsedDates.fromDate, parsedDates.toDate, barType);
      navigateToDetailedOrders(filters);
      return;
    }

    const fallbackRange = getFallbackDateRange();
    const filters = createFilters(fallbackRange.fromDate, fallbackRange.toDate, barType);
    navigateToDetailedOrders(filters);
  };

  const handleTrendBarClick = (data, barType) => {
    if (!data?.payload) return;

    const clickedData = data.payload;
    
    if (trendView === 'Week') {
      handleTrendBarClickWeek(clickedData.day, barType);
    } else {
      handleTrendBarClickMonth(clickedData.week, barType);
    }
  };

  const debugDateRange = () => {
    const dateRangeValues = getDateRange(dateRange);
    console.log('Current date range:', {
      fromDate: dateRangeValues.fromDate.format('YYYY-MM-DD'),
      toDate: dateRangeValues.toDate.format('YYYY-MM-DD'),
      rangeType: dateRange
    });
  };

  const handleSearch = () => {
    debugDateRange();
    fetchOrderStatusData();
    fetchOverviewData();
    fetchTrendData();
  };

  const handleOrderStatusClick = (labName, columnName, hospitalId) => {
    const statusMapping = {
      'totalOrders': '', 
      'uploaded': 'Completed',
      'partialUpload': 'Partial Completed', 
      'reportPending': 'Pending',
      'cancelled': 'Cancelled',
      'slaBreach': ''
    };

    const statusFilter = statusMapping[columnName];
    
    const filters = {
      hospitalId: hospitalId || '',
      orderStatus: statusFilter || ''
    };

    if (!hospitalId) {
      delete filters.hospitalId;
    }

    if (!statusFilter) {
      delete filters.orderStatus;
    }

    navigateToDetailedOrders(filters);
  };

  const handlePieChartClick = (data) => {
    if (data?.name) {
      const statusMapping = {
        'Completed': 'Completed',
        'Partial': 'Partial Completed',
        'Pending': 'Pending',
        'Cancelled': 'Cancelled'
      };

      const statusFilter = statusMapping[data.name];
      
      const filters = {
        orderStatus: statusFilter
      };

      navigateToDetailedOrders(filters);
    }
  };

  const handleTotalClick = (columnName) => {
    const statusMapping = {
      'totalOrders': '',
      'uploaded': 'Completed',
      'partialUpload': 'Partial Completed',
      'reportPending': 'Pending',
      'cancelled': 'Cancelled',
      'slaBreach': '' 
    };

    const statusFilter = statusMapping[columnName];
    
    const filters = {
      orderStatus: statusFilter || ''
    };

    if (!statusFilter) {
      delete filters.orderStatus;
    }

    navigateToDetailedOrders(filters);
  };

  const fetchOrderStatusData = async () => {
    try {
      const payload = buildCommonPayload();
      const response = await apiService.dashOrderStatus(payload);

      if (response.status === 200 && response?.data) {
        const statusData = response.data?.successList || [];
        setOrderStatusData(statusData);
      } else {
        console.error('Order Status API error:', response.statusText);
        setOrderStatusData([]);
      }
    } catch (error) {
      console.error('Order Status API request error:', error);
      setOrderStatusData([]);
    }
  };

  const fetchOverviewData = async () => {
    try {
      const payload = buildCommonPayload();
      const response = await apiService.dashOverview(payload);

      if (response.status === 200 && response?.data) {
        const overviewResponse = response.data?.successObject || {
          completed: 0,
          partial: 0,
          pending: 0,
          cancelled: 0,
          total: 0
        };
        
        setOverviewData(overviewResponse);

        const pieChartData = [
          { name: 'Completed', value: overviewResponse.completed || 0, color: '#0088FE' },
          { name: 'Partial', value: overviewResponse.partial || 0, color: '#00C49F' },
          { name: 'Pending', value: overviewResponse.pending || 0, color: '#FFBB28' },
          { name: 'Cancelled', value: overviewResponse.cancelled || 0, color: '#FF8042' },
        ];
        setOrderOverviewData(pieChartData);

      } else {
        console.error('Overview API error:', response.statusText);
        setOverviewData({
          completed: 0,
          partial: 0,
          pending: 0,
          cancelled: 0,
          total: 0
        });
        setOrderOverviewData([]);
      }
    } catch (error) {
      console.error('Overview API request error:', error);
      setOverviewData({
        completed: 0,
        partial: 0,
        pending: 0,
        cancelled: 0,
        total: 0
      });
      setOrderOverviewData([]);
    }
  };

  const fetchTrendData = async () => {
    if (trendView === 'Week') {
      await fetchTrendDataWeek();
    } else {
      await fetchTrendDataMonth();
    }
  };

  const fetchTrendDataWeek = async () => {
    try {
      const payload = buildCommonPayload();
      const response = await apiService.dashinflowWeek(payload);
      if (response.status === 200 && response?.data) {
        const weekData = response.data?.Days || [];
        
        const dateRangeValues = getDateRange(dateRange);
        
        const transformedData = weekData.map((day, index) => {
          let actualDate = null;
          
          if (day.actualDate) {
            actualDate = dayjs(day.actualDate).format('YYYY-MM-DD');
          } else {
            const calculatedDate = dateRangeValues.fromDate.add(index, 'day');
            if (calculatedDate <= dateRangeValues.toDate) {
              actualDate = calculatedDate.format('YYYY-MM-DD');
            }
          }
          
          return {
            day: day.dateName,
            ordersReceived: day.orderReceived || 0,
            reportsUploaded: day.ReportUploaded || 0,
            actualDate: actualDate
          };
        });

        setTrendData(transformedData);

        const totalData = response.data?.Total || {};
        const totalOrders = totalData.OrderReceived || 0;
        const totalUploaded = totalData.ReportUploaded || 0;
        const completionRate = totalOrders > 0 ? Math.round((totalUploaded / totalOrders) * 100) : 0;

        setTrendStats({
          ordersReceived: totalOrders,
          reportsUploaded: totalUploaded,
          completionRate: completionRate
        });

      } else {
        console.error('Week Trend API error:', response.statusText);
        setTrendData([]);
        setTrendStats({
          ordersReceived: 0,
          reportsUploaded: 0,
          completionRate: 0
        });
      }
    } catch (error) {
      console.error('Week Trend API request error:', error);
      setTrendData([]);
      setTrendStats({
        ordersReceived: 0,
        reportsUploaded: 0,
        completionRate: 0
      });
    }
  };

  const fetchTrendDataMonth = async () => {
    try {
      const payload = buildCommonPayload();
      const response = await apiService.dashinflowMonth(payload);
      if (response.status === 200 && response?.data) {
        const monthData = response.data?.Weeks || [];
        
        const transformedData = monthData.map(week => {
          let fromDate = null;
          let toDate = null;
          
          if (week.fromDate && week.toDate) {
            fromDate = dayjs(week.fromDate);
            toDate = dayjs(week.toDate);
          } else if (week.startDate && week.endDate) {
            fromDate = dayjs(week.startDate);
            toDate = dayjs(week.endDate);
          } else if (week.weekStart && week.weekEnd) {
            fromDate = dayjs(week.weekStart);
            toDate = dayjs(week.weekEnd);
          }
          
          return {
            week: week.weekLabel,
            ordersReceived: week.orderReceived || 0,
            reportsUploaded: week.orderUploaded || 0,
            fromDate: fromDate,
            toDate: toDate,
            rawFromDate: week.fromDate || week.startDate || week.weekStart,
            rawToDate: week.toDate || week.endDate || week.weekEnd
          };
        });

        setTrendData(transformedData);

        const totalData = response.data?.Total || {};
        const totalOrders = totalData.OrderReceived || 0;
        const totalUploaded = totalData.OrderUploaded || 0;
        const completionRate = totalOrders > 0 ? Math.round((totalUploaded / totalOrders) * 100) : 0;

        setTrendStats({
          ordersReceived: totalOrders,
          reportsUploaded: totalUploaded,
          completionRate: completionRate
        });

      } else {
        console.error('Month Trend API error:', response.statusText);
        setTrendData([]);
        setTrendStats({
          ordersReceived: 0,
          reportsUploaded: 0,
          completionRate: 0
        });
      }
    } catch (error) {
      console.error('Month Trend API request error:', error);
      setTrendData([]);
      setTrendStats({
        ordersReceived: 0,
        reportsUploaded: 0,
        completionRate: 0
      });
    }
  };

  const handleTrendViewChange = (view) => {
    setTrendView(view);
  };

  const getLabList = async () => {
    setLabList([]);
    setSearchFilters(prev => ({ ...prev, selectedLabs: [] }));
    
    setLabSearchLoading(true);
    try {
      const payload = { type: searchFilters?.testType || 'All' };
      const response = await apiService.getlabsByType(payload);
      if (response?.status === 200) {
        const labs = response.data || [];
        const allOption = { labCode: "All", labName: "All" };
        setLabList([allOption, ...labs]);
      }
    } catch (error) {
      console.error('Error fetching lab list:', error);
      setLabList([]);
    } finally {
      setLabSearchLoading(false);
    }
  };

  const getEclinicsData = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_COMMON_URL}EclinicAddress/list?eclinicName=${searchTerm}`,
        { headers: { 'Authorization': `Bearer ${s_token}` } }
      );

      if (response?.data?.data) {
        const eclinics = response.data.data || [];
        setEclinicData(eclinics);
      }
    } catch (error) {
      console.error("Error fetching eClinics:", error);
      setEclinicData([]);
    }
  };

  const getCCList = async () => {
    setCcSearchLoading(true);
    try {
      const payload = { diagnosticTypes: "Collection Center" };
      const response = await apiService.getCCList(payload);
      if (response?.data?.successList) {
        const ccs = response.data.successList;
        const allOption = { labCode: "All", labName: "All" };
        setCcList([allOption, ...ccs]);
      }
    } catch (error) {
      console.error("Error fetching CC list:", error);
      setCcList([]);
    } finally {
      setCcSearchLoading(false);
    }
  };

  const handleReset = () => {
    setOrderType([ORDER_TYPE_OPTIONS[0]]);
    setDateRange("last7");
    setSearchFilters({
      fromDate: dayjs().subtract(7, 'day'),
      toDate: dayjs(),
      testType: 'All',
      selectedEclinics: [],
      selectedLabs: [],
      selectedCcs: []
    });
    setEclinicData([]);
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'custom') {
      const dateRangeValues = getDateRange(value);
      setSearchFilters(prev => ({
        ...prev,
        fromDate: dateRangeValues.fromDate,
        toDate: dateRangeValues.toDate
      }));
    }
  };

  const handleEclinicChange = (event, newValue) => {
    setSearchFilters((prev) => ({ ...prev, selectedEclinics: newValue }));
  };

  const handleEclinicInputChange = (event, value) => {
    setEclinicData([]);
    if (value.length > 2) {
      getEclinicsData(value);
    }
  };

  const handleLabChange = (newValue) => {
    setSearchFilters((prev) => ({ ...prev, selectedLabs: newValue }));
  };

  const handleCcChange = (newValue) => {
    setSearchFilters((prev) => ({ ...prev, selectedCcs: newValue }));
  };

  const handleOrderTypeChange = (newValue) => {
    setOrderType(newValue);
  };

  const calculateTotals = () => {
    if (orderStatusData.length === 0) {
      return {
        totalOrders: 0,
        uploaded: 0,
        manualUploaded: 0,
        automaticUploaded: 0,
        partialUpload: 0,
        reportPending: 0,
        cancelled: 0,
        slaBreach: 0
      };
    }

    return orderStatusData.reduce((acc, item) => ({
      totalOrders: acc.totalOrders + (item.totalOrder || 0),
      uploaded: acc.uploaded + (item.uploaded || 0),
      manualUploaded: acc.manualUploaded + (Number(item.manualUploaded ?? item.manualUpload) || 0),
      automaticUploaded: acc.automaticUploaded + (Number(item.automaticUploaded ?? item.autoUploaded ?? item.automaticUpload) || 0),
      partialUpload: acc.partialUpload + (item.partialUpload || 0),
      reportPending: acc.reportPending + (item.reportPending || 0),
      cancelled: acc.cancelled + (item.cancelled || 0),
      slaBreach: acc.slaBreach + (item.slaBreach || 0)
    }), {
      totalOrders: 0,
      uploaded: 0,
      manualUploaded: 0,
      automaticUploaded: 0,
      partialUpload: 0,
      reportPending: 0,
      cancelled: 0,
      slaBreach: 0
    });
  };

  useEffect(() => {
    handleSearch();
    getCCList();
  }, []);

  useEffect(() => {
    if (searchFilters.testType) {
      getLabList();
    }
  }, [searchFilters.testType]);

  useEffect(() => {
    fetchTrendData();
  }, [trendView]);

    const orderTypeValues = new Set(orderType.map(opt => opt.value));
  const shouldShowEclinicFilter = orderTypeValues.has("All") || orderTypeValues.has("Lab") || orderTypeValues.has("Collection Center") || orderTypeValues.has("b2b2C");
  const shouldShowLabFilter = orderTypeValues.has("All") || orderTypeValues.has("Lab") || orderTypeValues.has("d2c") || orderTypeValues.has("Collection Center") || orderTypeValues.has("b2b2C");
  const shouldShowCcFilter = orderTypeValues.has("All") || orderTypeValues.has("Collection Center") || orderTypeValues.has("b2b2C");
  const xAxisKey = trendView === 'Week' ? 'day' : 'week';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="p-2 sm:p-3 space-y-2.5 bg-gray-50 min-h-screen">
        <Card
          className="w-full"
          sx={{
            position: "sticky",
            top: 64,
            zIndex: 25,
            bgcolor: "#fff",
            borderRadius: "10px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.25 }, "&:last-child": { pb: 1.25 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#0F172A" }}>
                  Orders Dashboard
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                  sx={{ borderRadius: "6px", textTransform: "none", fontSize: "0.8125rem", height: 30, px: 1.25, borderColor: "#CBD5E1", color: "#475569" }}
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </Box>

              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={dateRange}
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                    sx={{
                      height: 32,
                      borderRadius: "7px",
                      bgcolor: "#ffffff",
                      fontSize: "0.8125rem",
                      "& .MuiSelect-select": { py: "4px !important" },
                      "& fieldset": { borderColor: "#CBD5E1" },
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                    }}
                  >
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.8125rem" }}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {dateRange === 'custom' && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <DatePicker
                      label="From Date"
                      value={searchFilters.fromDate}
                      onChange={(newValue) => setSearchFilters({ ...searchFilters, fromDate: newValue })}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: 130, "& .MuiOutlinedInput-root": { height: 32, borderRadius: "7px", fontSize: "0.8125rem" } },
                        },
                      }}
                    />
                    <DatePicker
                      label="To Date"
                      value={searchFilters.toDate}
                      onChange={(newValue) => setSearchFilters({ ...searchFilters, toDate: newValue })}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: 130, "& .MuiOutlinedInput-root": { height: 32, borderRadius: "7px", fontSize: "0.8125rem" } },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(5, 1fr)",
                  xl: "repeat(5, 1fr)",
                },
                gap: 1,
                alignItems: "center",
              }}
            >
              <Box>
                <MultiSelectDropdown
                  options={ORDER_TYPE_OPTIONS}
                  value={orderType}
                  onChange={handleOrderTypeChange}
                  label="Order Type"
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                />
              </Box>

              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 }, "&.Mui-focused": { color: "#004c97" } }}>Test Type</InputLabel>
                  <Select
                    value={searchFilters.testType}
                    onChange={(e) => setSearchFilters({ ...searchFilters, testType: e.target.value })}
                    label="Test Type"
                    sx={{
                      height: 34,
                      borderRadius: "7px",
                      bgcolor: "#ffffff",
                      fontSize: "0.8125rem",
                      "& .MuiSelect-select": { py: "4px !important", display: "flex", alignItems: "center" },
                      "& fieldset": { borderColor: "#CBD5E1" },
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
                    }}
                  >
                    {TEST_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.8125rem" }}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {shouldShowEclinicFilter && (
                <Box>
                  <Autocomplete
                    multiple
                    size="small"
                    options={eclinicData || []}
                    getOptionLabel={(option) => option?.eclinicName || ""}
                    isOptionEqualToValue={(option, val) =>
                      (option?.eclinicCode && val?.eclinicCode ? option.eclinicCode === val.eclinicCode : option?.eclinicName === val?.eclinicName)
                    }
                    value={searchFilters.selectedEclinics}
                    onChange={handleEclinicChange}
                    onInputChange={handleEclinicInputChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="eClinics"
                        size="small"
                        placeholder="Select eClinics"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: 34, borderRadius: "7px", bgcolor: "#ffffff", fontSize: "0.8125rem", py: "2px !important",
                            "& fieldset": { borderColor: "#CBD5E1" },
                            "&:hover fieldset": { borderColor: "#94A3B8" },
                            "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
                          },
                          "& .MuiInputLabel-root": { fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 } },
                          "& .MuiInputLabel-root.Mui-focused": { color: "#004c97" },
                        }}
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} style={{ fontSize: "0.8125rem" }}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                          size="small"
                        />
                        {option.eclinicName}
                      </li>
                    )}
                    sx={{
                      "& .MuiAutocomplete-tag": {
                        maxWidth: "100%",
                        fontSize: "0.75rem",
                        m: "2px",
                      },
                    }}
                  />
                </Box>
              )}

              {shouldShowLabFilter && (
                <Box>
                  <SearchableMultiSelectDropdown
                    options={labList || []}
                    value={searchFilters.selectedLabs}
                    onChange={handleLabChange}
                    label="Lab"
                    getOptionLabel={(opt) => opt.labName}
                    getOptionValue={(opt) => opt.labCode}
                    disabled={labSearchLoading}
                  />
                </Box>
              )}

              <Box sx={{ height: 34 }}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: "#004c97",
                    "&:hover": { bgcolor: "#00376d" },
                    textTransform: "none",
                    borderRadius: "7px",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    height: "100%",
                    boxShadow: "none",
                  }}
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card className="w-full shadow-md rounded-lg border border-gray-200 bg-[#F6FAFE]">
          <CardContent className="p-5">
            <div className="flex justify-between items-center mb-4">
              <Typography variant="h6" className="font-extrabold text-gray-800">
                Order Status
              </Typography>
            </div>

            <div className="overflow-x-auto">
              <TableContainer
                component={Paper}
                elevation={0}
                className="border border-gray-200 rounded-lg"
                sx={{
                  minWidth: 800,
                  maxHeight: 420,
                  overflow: "auto",
                  position: "relative",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow className="bg-[#F0F6FF]">
                      <TableCell
                        className="font-bold text-gray-700 text-sm sticky left-0 bg-[#F0F6FF] z-20"
                        sx={{ minWidth: 160 }}
                      >
                        Lab Name
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Total Orders Received
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Reports Uploaded
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Manual Uploaded
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Automatic Uploaded
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Partial Uploaded
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Reports Pending
                      </TableCell>
                      <TableCell className="font-bold text-gray-700 text-sm text-center">
                        Cancelled Orders
                      </TableCell>
                      {/* <TableCell className="font-bold text-gray-700 text-sm text-center">
                        SLA Breach
                      </TableCell> */}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {orderStatusData.length > 0 && (
                      <TableRow
                        className="sticky top-[38px] bg-[#F0F6FF] font-semibold text-green-600"
                        sx={{ position: "sticky", top: 38, zIndex: 10 }}
                      >
                        <TableCell
                          className="font-bold text-gray-800 sticky left-0 bg-[#F0F6FF] z-20"
                          sx={{ minWidth: 160 }}
                        >
                          Total
                        </TableCell>
                        <TableCell 
                          align="center"
                          onClick={() => handleTotalClick('totalOrders')}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleTotalClick('totalOrders')}
                          tabIndex={0}
                          role="button"
                        >
                          {calculateTotals().totalOrders}
                        </TableCell>
                        <TableCell 
                          align="center"
                          onClick={() => handleTotalClick('uploaded')}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleTotalClick('uploaded')}
                          tabIndex={0}
                          role="button"
                        >
                          {calculateTotals().uploaded}
                        </TableCell>
                        <TableCell align="center">
                          {calculateTotals().manualUploaded}
                        </TableCell>
                        <TableCell align="center">
                          {calculateTotals().automaticUploaded}
                        </TableCell>
                        <TableCell 
                          align="center"
                          onClick={() => handleTotalClick('partialUpload')}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleTotalClick('partialUpload')}
                          tabIndex={0}
                          role="button"
                        >
                          {calculateTotals().partialUpload}
                        </TableCell>
                        <TableCell 
                          align="center"
                          onClick={() => handleTotalClick('reportPending')}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleTotalClick('reportPending')}
                          tabIndex={0}
                          role="button"
                        >
                          {calculateTotals().reportPending}
                        </TableCell>
                        <TableCell 
                          align="center"
                          onClick={() => handleTotalClick('cancelled')}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleTotalClick('cancelled')}
                          tabIndex={0}
                          role="button"
                        >
                          {calculateTotals().cancelled}
                        </TableCell>
                        {/* <TableCell 
                          align="center"
                          onClick={() => handleTotalClick('slaBreach')}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleTotalClick('slaBreach')}
                          tabIndex={0}
                          role="button"
                        >
                          {calculateTotals().slaBreach || 0}
                        </TableCell> */}
                      </TableRow>
                    )}

                    {orderStatusData.map((row) => (
                      <TableRow
                        key={row.hospitalId}
                        className="bg-white hover:bg-blue-50"
                      >
                        <TableCell
                          className="font-medium text-gray-800 text-sm sticky left-0 z-10"
                          sx={{
                            minWidth: 160,
                            backgroundColor: "#ffffff",
                          }}
                        >
                          {row.hospitalName || "Unknown Lab"}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          onClick={() => handleOrderStatusClick(row.hospitalName, 'totalOrders', row.hospitalId)}
                          className="text-blue-700 underline cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderStatusClick(row.hospitalName, 'totalOrders', row.hospitalId)}
                          tabIndex={0}
                          role="button"
                        >
                          {row.totalOrder || 0}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          onClick={() => handleOrderStatusClick(row.hospitalName, 'uploaded', row.hospitalId)}
                          className="text-blue-700 underline cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderStatusClick(row.hospitalName, 'uploaded', row.hospitalId)}
                          tabIndex={0}
                          role="button"
                        >
                          {row.uploaded || 0}
                        </TableCell>
                        <TableCell align="center">
                          {row.manualUploaded ?? row.manualUpload ?? 0}
                        </TableCell>
                        <TableCell align="center">
                          {row.automaticUploaded ?? row.autoUploaded ?? row.automaticUpload ?? 0}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          onClick={() => handleOrderStatusClick(row.hospitalName, 'partialUpload', row.hospitalId)}
                          className="text-blue-700 underline cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderStatusClick(row.hospitalName, 'partialUpload', row.hospitalId)}
                          tabIndex={0}
                          role="button"
                        >
                          {row.partialUpload || 0}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          onClick={() => handleOrderStatusClick(row.hospitalName, 'reportPending', row.hospitalId)}
                          className="text-blue-700 underline cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderStatusClick(row.hospitalName, 'reportPending', row.hospitalId)}
                          tabIndex={0}
                          role="button"
                        >
                          {row.reportPending || 0}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          onClick={() => handleOrderStatusClick(row.hospitalName, 'cancelled', row.hospitalId)}
                          className="text-blue-700 underline cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderStatusClick(row.hospitalName, 'cancelled', row.hospitalId)}
                          tabIndex={0}
                          role="button"
                        >
                          {row.cancelled || 0}
                        </TableCell>
                        {/* <TableCell 
                          align="center" 
                          onClick={() => handleOrderStatusClick(row.hospitalName, 'slaBreach', row.hospitalId)}
                          className="text-blue-700 underline cursor-pointer hover:bg-blue-50 transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderStatusClick(row.hospitalName, 'slaBreach', row.hospitalId)}
                          tabIndex={0}
                          role="button"
                        >
                          {row.slaBreach || 0}
                        </TableCell> */}
                      </TableRow>
                    ))}

                    {orderStatusData.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          align="center"
                          className="py-4 text-gray-500"
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="shadow-md rounded-lg border border-gray-200 bg-[#F6FAFF]">
            <CardContent className="p-5">
              <Typography variant="h6" className="font-bold text-gray-800 mb-10">
                Order Overview
              </Typography>

              <div className="flex flex-wrap justify-center gap-4 mb-3 mt-7">
                {orderOverviewData.map((item) => (
                  <div key={item.name} className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-gray-700 text-sm font-semibold">
                      {item.name}{" "}
                      <span className="font-bold text-gray-900">{item.value}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="h-56 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orderOverviewData}
                      dataKey="value"
                      innerRadius={70}
                      outerRadius={110}
                      startAngle={210}
                      endAngle={-30}
                      paddingAngle={2}
                      onClick={handlePieChartClick}
                    >
                      {orderOverviewData.map((entry) => (
                        <Cell 
                          key={entry.name} 
                          fill={entry.color}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>

                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                  <p className="text-gray-600 text-sm font-medium">Total</p>
                  <p className="text-3xl font-bold text-[#013A63]">
                    {overviewData?.total}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md rounded-lg border border-gray-200">
            <CardContent className="p-3">
              <div className="flex justify-between items-center mb-3">
                <Typography variant="h6" className="font-bold text-gray-800">
                  Order Inflow vs Report Upload Trend
                </Typography>
                <div className="flex gap-2">
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <Button 
                      size="small" 
                      className={`text-xs ${trendView === 'Week' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-600 hover:bg-white'}`}
                      onClick={() => handleTrendViewChange('Week')}
                    >
                      Week
                    </Button>
                    <Button 
                      size="small" 
                      className={`text-xs ${trendView === 'Month' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-600 hover:bg-white'}`}
                      onClick={() => handleTrendViewChange('Month')}
                    >
                      Month
                    </Button>
                  </div>
                </div>
              </div>

              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={trendData}
                    margin={{ top: 15, right: 20, left: 15, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey={xAxisKey} 
                      stroke="#666" 
                      fontSize={11}
                    />
                    <YAxis 
                      stroke="#666" 
                      fontSize={11}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="ordersReceived" 
                      name={`Orders Received (${trendStats.ordersReceived})`}
                      fill="#0183D2"
                      radius={[3, 3, 0, 0]}
                      onClick={(data) => handleTrendBarClick(data, 'ordersReceived')}
                      style={{ cursor: 'pointer' }}
                    />
                    <Bar 
                      dataKey="reportsUploaded" 
                      name={`Reports Uploaded (${trendStats.reportsUploaded})`}
                      fill="#03F15A"
                      radius={[3, 3, 0, 0]}
                      onClick={(data) => handleTrendBarClick(data, 'reportsUploaded')}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </LocalizationProvider>
    
  
  
  );
}